import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState,
} from "react";

import { type EmblaCarouselType } from "embla-carousel";

import { cn } from "@/shared-web.ui/util.cn";

type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};

export const useDotButton = (
  emblaApi: EmblaCarouselType | undefined,
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((emApi: EmblaCarouselType) => {
    setScrollSnaps(emApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emApi: EmblaCarouselType) => {
    setSelectedIndex(emApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

interface DotButtonProps extends ComponentPropsWithRef<"button"> {
  isActive: boolean;
}
export const DotButton = (props: DotButtonProps) => {
  const { children, className, isActive, ...restProps } = props;

  return (
    <button
      type="button"
      className={cn(
        `
          rounded-2 border border-gray-6 size-8 text-2 flex
          items-center justify-center
        `,
        isActive && `
          outline outline-2 outline-offset-2
          outline-accent-8
        `,
      )}
      {...restProps}
    >
      {children}
    </button>
  );
};
